import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormArray, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatTableModule } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { DialogService } from '@adgate/common/services/dialog-service';
import { ObjectDescPipe } from '@adgate/common/pipes/object-desc-pipe';
import { ProductionRequest } from '@adgate/modules/production/types/production-request.type';
import { ProductionRequestSample } from '@adgate/modules/production/types/production-request-sample.type';
import { ProductionService } from '@adgate/modules/production/services/production-service';
import { RESTApplicationResponse } from '@adgate/common/types/rest-application-response.type';
import { ComparatorService } from '@adgate/common/services/comparator-service';
import { CommonObjectService } from '@adgate/common/services/common-object-service';
import { Status } from '@adgate/common/types/status.type';
import { forkJoin } from 'rxjs';
import { NetworkService } from '@adgate/common/services/network-service';
import { saveAs } from 'file-saver';
import { environment } from '@env/environment';
import { StateService } from '@adgate/common/services/state-service';

@Component({
  selector: 'app-production-request-sample-dialog-component',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatTableModule,
    MatFormFieldModule,
    MatSelectModule,
    MatTooltip,
    ObjectDescPipe
  ],
  templateUrl: './production-request-sample-dialog-component.html',
  styleUrl: './production-request-sample-dialog-component.scss',
})
export class ProductionRequestSampleDialogComponent implements OnInit
{
  private dialogRef: MatDialogRef<ProductionRequestSampleDialogComponent> = inject(MatDialogRef<ProductionRequestSampleDialogComponent>);
  private fb: FormBuilder = inject(FormBuilder);
  private dialogService: DialogService = inject(DialogService);
  private productionService: ProductionService = inject(ProductionService);
  private commonObjectService: CommonObjectService = inject(CommonObjectService);
  private networkService: NetworkService = inject(NetworkService);
  private stateService: StateService = inject(StateService);
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  public comparatorService: ComparatorService = inject(ComparatorService);  
  public data: ProductionRequest = inject(MAT_DIALOG_DATA, { optional: false });

  sampleForm: FormGroup;
  displayedColumns: string[] = ['filename', 'status', 'actions'];
  tableDataSource: any[] = [];
  statuses: Status[] = [];

  constructor()
  {
    this.sampleForm = this.fb.group({
      rows: this.fb.array([])
    });
  }

  ngOnInit(): void
  {
    this.dialogService.showProgress();
    forkJoin([
      this.productionService.getProductionRequestSamples(this.data.id),
      this.commonObjectService.getStatuses(ProductionRequestSample.OBJECT_TYPE)
    ]).subscribe({
      next: (responses: RESTApplicationResponse[]) => {
        const samples = responses[0].data;
        this.statuses = responses[1].data;

        if (samples.length == 0) {
          this.dialogService.hideProgress();
          this.dialogService.openInfoDialog("There are no samples associated with this production request");
          this.dialogRef.close(null);
        } else {
          // Initialize FormArray with samples
          samples.forEach((sample: ProductionRequestSample) => {
            this.rows.push(this.createRowGroup(sample));
          });
          this.updateTableDataSource();
          this.cdr.detectChanges();
          this.dialogService.hideProgress();
        }
      },
      error: (err: Error) => {
        this.dialogService.hideProgress();
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  private createRowGroup(sample: ProductionRequestSample): FormGroup
  {
    return this.fb.group({
      id: [sample.id],
      fileName: [sample.fileName],
      status: [{value: sample.status, disabled: this.data.status.id != Status.PRODUCTION_REQUEST_AWAITINGSAMPLEAPPROVAL}, Validators.required],
      originalSample: [sample] // Keep original for reference
    });
  }

  get rows(): FormArray
  {
    return this.sampleForm.get('rows') as FormArray;
  }

  private updateTableDataSource(): void
  {
    this.tableDataSource = [...this.rows.controls];
  }

  onDownloadSample(row: FormGroup): void
  {
    const sample:ProductionRequestSample = row.get('originalSample')?.value;
    const url = `${this.productionService.getBaseServiceUrl()}/downloadProductionRequestSample?sample=${btoa(sample.fileName)}&x-auth-token=${atob(environment.idHash)}&x-user-token=${this.stateService.data.userToken}`;

    this.dialogService.showProgress();    
    this.networkService.downloadExternalFile(`${url}&stream=false`).subscribe({
      next: (res:Blob) => {
        // Infer file extension from content type
        let filename = sample.fileName;
        saveAs(res, filename);
        this.dialogService.hideProgress();
      },
      error: (err:Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  onCancel(): void
  {
    this.dialogRef.close(null);
  }

  allowSave()
  {
    return this.sampleForm.valid && this.data.status.id == Status.PRODUCTION_REQUEST_AWAITINGSAMPLEAPPROVAL;
  }

  onSave(): void
  {
    if (this.sampleForm.valid) {
      const approvedSampleCount = this.rows.getRawValue().filter((row: any) => row.status?.id === Status.PRODUCTION_REQUESTSAMPLE_APPROVED).length;

      if( approvedSampleCount > 1 ) {
        this.dialogService.openErrorDialog("Only one sample can be approved");
        return;
      } else {
        const updatedSamples = this.rows.controls.map((row: any) => ({
          id: row.get('id')?.value,
          status: row.get('status')?.value
        }));

        this.dialogRef.close(updatedSamples);
      }
    }
  }
}
